import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const btn = this.refs.addToCartButton;
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const form =
      /** @type {HTMLFormElement | null} */ (this.closest('form')) ||
      (productForm ? productForm.querySelector('form') : null);

    // Match upstream Horizon: PDP uses type="submit" — after this handler the browser fires `submit`,
    // and `product-form-component` capture listener runs AJAX (`#onNativeFormSubmitCapture`).
    // Forcing type="button" on PDP removed that path; any early return here meant zero network requests.
    if (form && !form.checkValidity()) {
      event.preventDefault();
      form.reportValidity();
      return;
    }

    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      if (!validation.canAdd) {
        event.preventDefault();
        return;
      }
    }

    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }

    // Quick-add card uses type="button" (no implicit submit) — must call handleSubmit here.
    if (btn.type === 'button' && productForm && typeof productForm.handleSubmit === 'function') {
      event.preventDefault();
      productForm.handleSubmit(new SubmitEvent('submit', { bubbles: true, cancelable: true, submitter: btn }));
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1); 
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** Clears stuck “variant changing” state if section fetch/morph never dispatches variant:update. */
  #variantChangeSafetyTimer = 0;

  /** @type {Array<{variantId: string, quantity: number}>} */
  #addToCartQueue = [];

  /** @type {HTMLFormElement | null} */
  #nativeSubmitForm = null;

  /**
   * When delegated `on:submit` misses (timing, edge browsers), the native `{% form 'product' %}` POST still runs and sends shoppers to `/cart`. Capture on the form guarantees we stay on the page and use the AJAX path.
   * @param {SubmitEvent} event
   */
  #onNativeFormSubmitCapture = (event) => {
    if (!(event.target instanceof HTMLFormElement) || !this.contains(event.target)) return;
    if (event.defaultPrevented) return;
    event.preventDefault();
    this.handleSubmit(event);
  };

  /**
   * Read max from fetched product HTML — `max` content attribute is reliable; IDL `input.max` is often empty on DOMParser output.
   * @param {HTMLInputElement | null} input
   * @returns {string | null}
   */
  #readQuantityMaxFromFetchedInput(input) {
    if (!input) return null;
    const fromAttr = input.getAttribute('max');
    if (fromAttr != null && fromAttr !== '') return fromAttr;
    const idl = input.max;
    if (idl != null && idl !== '') return idl;
    return null;
  }

  connectedCallback() {
    super.connectedCallback();

    const form = /** @type {HTMLFormElement | null} */ (this.querySelector('form'));
    if (form) {
      this.#nativeSubmitForm = form;
      form.addEventListener('submit', this.#onNativeFormSubmitCapture, { capture: true });
    }

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
    target?.addEventListener(ThemeEvents.variantFetchFailed, this.#onVariantFetchFailed, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    this.#nativeSubmitForm?.removeEventListener('submit', this.#onNativeFormSubmitCapture, { capture: true });
    this.#nativeSubmitForm = null;

    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches the current cart and updates the quantity display.
   * @returns {Promise<Cart | null>} The cart object, or null if fetch fails.
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return null;

    const cartJsUrl =
      (typeof globalThis !== 'undefined' && globalThis.Theme?.routes?.cart_js_url) ||
      (typeof Theme !== 'undefined' && Theme?.routes?.cart_js_url) ||
      '/cart.js';

    try {
      const response = await fetch(cartJsUrl, { credentials: 'same-origin' });
      const cart = await response.json();

      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return null;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const currentId = this.refs.variantId?.value;
      // Hidden input already matches selection but variant:update is late/missing — add anyway.
      if (
        intendedVariantId &&
        currentId &&
        String(intendedVariantId) === String(currentId)
      ) {
        this.#clearVariantChangeSafetyTimer();
        this.#variantChangeInProgress = false;
        this.#addToCartQueue = [];
        this.#processAddToCart(undefined, undefined, event);
        return;
      }

      const quantity = this.#getQuantity();

      if (intendedVariantId) {
        this.#addToCartQueue.push({ variantId: intendedVariantId, quantity });
      }

      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    const fromUrl = new URL(window.location.href).searchParams.get('variant');
    if (fromUrl) return fromUrl;

    const section = this.closest('.shopify-section, dialog, product-card');
    const pickerRoot = section?.querySelector('variant-picker, swatches-variant-picker-component');
    if (pickerRoot) {
      const checked = pickerRoot.querySelector('input[type="radio"]:checked');
      if (checked instanceof HTMLInputElement && checked.dataset.variantId) {
        return checked.dataset.variantId;
      }
      for (const sel of pickerRoot.querySelectorAll('select.variant-option__select')) {
        if (!(sel instanceof HTMLSelectElement)) continue;
        const opt = sel.options[sel.selectedIndex];
        if (opt?.dataset?.variantId) return opt.dataset.variantId;
      }
    }

    return this.refs.variantId?.value || undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /**
   * True when this add-to-cart host is the one a shopper can use (visible, enabled, not quick-add “choose” decoy).
   * @param {AddToCartComponent} container
   */
  #isEligibleAddToCartContainer(container) {
    const btn = container.refs.addToCartButton;
    if (!btn || btn.disabled) return false;
    const host = /** @type {HTMLElement | null} */ (container);
    if (!(host instanceof HTMLElement)) return false;
    if (!this.#isAddToCartUiDisplayed(host)) return false;
    const quick = host.closest('quick-add-component');
    if (quick?.getAttribute('data-quick-add-button') === 'choose') {
      const hiddenAddHost = quick.querySelector('add-to-cart-component');
      if (hiddenAddHost === host) return false;
    }
    return true;
  }

  /** @param {HTMLElement} host */
  #isAddToCartUiDisplayed(host) {
    if (host.hasAttribute('hidden')) return false;
    if (host.closest('[hidden]')) return false;
    const style = getComputedStyle(host);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    return true;
  }

  /**
   * Shopify `/cart/add.js` success shapes vary: full cart `{ items, item_count }`, or a **single line item**
   * (`variant_id`, `quantity`, `key`, …) with no `items` wrapper — treating only `items`/`item_count` breaks adds.
   * @param {Record<string, unknown> | null | undefined} parsed
   */
  #looksLikeCartAddSuccess(parsed) {
    if (!parsed || typeof parsed !== 'object') return false;
    const errs = parsed.errors;
    if (errs != null && errs !== false && errs !== '') {
      if (Array.isArray(errs) && errs.length === 0) {
        /* ok */
      } else if (typeof errs === 'object' && !Array.isArray(errs) && Object.keys(errs).length === 0) {
        /* ok — some apps send errors: {} on success */
      } else {
        return false;
      }
    }
    const httpStatus = parsed.status;
    if (typeof httpStatus === 'number' && httpStatus >= 400) return false;
    if (Array.isArray(parsed.items)) return true;
    if (typeof parsed.item_count === 'number') return true;
    const vid = parsed.variant_id;
    const hasVariant =
      (typeof vid === 'number' && Number.isFinite(vid)) || (typeof vid === 'string' && String(vid).trim() !== '');
    const qtyNum = Number(parsed.quantity);
    if (hasVariant && Number.isFinite(qtyNum) && qtyNum >= 0) return true;
    if (hasVariant && typeof parsed.key === 'string' && parsed.key.length > 0) return true;
    return false;
  }

  /**
   * Prefer the product form `action` so locale/market paths match the session (Theme.routes can be wrong in edge cases).
   * @param {HTMLFormElement | null | undefined} form
   */
  #resolveCartAddJsUrl(form) {
    if (form instanceof HTMLFormElement) {
      try {
        // IDL `form.action` is the browser-resolved absolute URL (handles locale-prefixed `/de/cart/add` when Liquid sets it).
        const u = new URL(form.action);
        if (u.pathname.endsWith('/cart/add.js')) {
          return u.pathname + u.search + u.hash;
        }
        if (/\/cart\/add\/?$/.test(u.pathname)) {
          u.pathname = u.pathname.replace(/\/cart\/add\/?$/, '/cart/add.js');
          return u.pathname + u.search + u.hash;
        }
      } catch {
        /* use Theme fallback */
      }
    }
    const addFromTheme =
      (typeof globalThis !== 'undefined' && globalThis.Theme?.routes?.cart_add_url) ||
      (typeof Theme !== 'undefined' && Theme?.routes?.cart_add_url);
    if (addFromTheme) return addFromTheme;
    return '/cart/add.js';
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    // Require at least one visible enabled add control — do not let a duplicate/decoy disabled button block.
    if (!overrideVariantId) {
      const canSubmit = Array.from(allAddToCartContainers).some((c) => this.#isEligibleAddToCartContainer(c));
      if (!canSubmit) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);
    const variantIdInput =
      /** @type {HTMLInputElement | undefined} */ (this.refs.variantId) ||
      /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]')) ||
      undefined;
    const idFromDom = String(variantIdInput?.value ?? '').trim();
    if (!overrideVariantId && idFromDom && !String(formData.get('id') ?? '').trim()) {
      formData.set('id', idFromDom);
    }

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    // Do not send Cart API `sections` from the product page. Bundled section rendering uses the
    // Referer URL as context; passing header/drawer `section.id` values from a PDP often yields
    // HTTP 400 and the line item never adds. Drawer refreshes via `sectionRenderer.renderSection`
    // when `CartAddEvent` has no embedded section HTML (see component-cart-items.js).

    const fetchCfg = fetchConfig('javascript', { body: formData });

    const cartAddUrl = this.#resolveCartAddJsUrl(form);

    fetch(cartAddUrl, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
      },
      credentials: 'same-origin',
    })
      .then(async (fetchResponse) => {
        const text = await fetchResponse.text();
        let parsed;
        try {
          parsed = /** @type {Record<string, unknown>} */ (JSON.parse(text));
        } catch {
          console.error('[product-form] /cart/add response was not JSON', fetchResponse.status, text.slice(0, 400));
          throw new Error(
            fetchResponse.ok
              ? 'Cart add returned non-JSON (check storefront apps / theme dev proxy).'
              : `Cart add failed (HTTP ${fetchResponse.status}).`
          );
        }

        const ok = fetchResponse.ok && this.#looksLikeCartAddSuccess(parsed);

        if (!ok) {
          const msg =
            (typeof parsed.message === 'string' && parsed.message) ||
            (typeof parsed.description === 'string' && parsed.description) ||
            `Cart add failed (HTTP ${fetchResponse.status})`;

          this.dispatchEvent(
            new CartErrorEvent(
              form.getAttribute('id') || '',
              msg,
              typeof parsed.description === 'string' ? parsed.description : undefined,
              parsed.errors
            )
          );

          if (!addToCartTextError) return null;
          addToCartTextError.classList.remove('hidden');
          const textNodeErr = addToCartTextError.childNodes[2];
          if (textNodeErr) {
            textNodeErr.textContent = msg;
          } else {
            addToCartTextError.appendChild(document.createTextNode(msg));
          }
          this.#setLiveRegionText(msg);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return null;
        }

        return parsed;
      })
      .then(async (response) => {
        if (!response) return;

        const id = formData.get('id');

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        if (!id) throw new Error('Form ID is required');

        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText =
            addedTextElement?.textContent?.trim() ||
            (typeof Theme !== 'undefined' ? Theme?.translations?.added : undefined) ||
            'Added';

          this.#setLiveRegionText(addedText);

          setTimeout(() => {
            this.#clearLiveRegionText();
          }, SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        let cart = await this.#fetchAndUpdateCartQuantity();
        if (!cart && typeof response.item_count === 'number') {
          cart = /** @type {Cart} */ (response);
        } else if (!cart && Array.isArray(response.items)) {
          cart = /** @type {Cart} */ ({ items: response.items });
        } else if (!cart && response.variant_id != null) {
          // Omit item_count so cart UI uses data.itemCount delta (item_count here was only "qty added").
          cart = /** @type {Cart} */ ({
            items: [response],
          });
        }

        this.dispatchEvent(
          new CartAddEvent(cart ?? undefined, id.toString(), {
            source: 'product-form-component',
            itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
            productId: this.dataset.productId,
          })
        );
      })
      .catch((error) => {
        console.error(error);
        const msg = error instanceof Error ? error.message : String(error);
        if (addToCartTextError && msg) {
          addToCartTextError.classList.remove('hidden');
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = msg;
          } else {
            addToCartTextError.appendChild(document.createTextNode(msg));
          }
          this.#setLiveRegionText(msg);
          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }
      })
      .finally(() => {
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /** @param {Array<{variantId: string, quantity: number}>} items */
  #processBatchAddToCart(items) {
    if (items.length === 0) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
      })),
    };

    const cartAddUrl = this.#resolveCartAddJsUrl(this.querySelector('form'));

    fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then(async (fetchResponse) => {
        const text = await fetchResponse.text();
        let parsed;
        try {
          parsed = JSON.parse(text);
        } catch {
          throw new Error(`Cart add failed (HTTP ${fetchResponse.status})`);
        }

        const ok = fetchResponse.ok && this.#looksLikeCartAddSuccess(parsed);

        if (!ok) {
          const msg =
            (typeof parsed.message === 'string' && parsed.message) ||
            (typeof parsed.description === 'string' && parsed.description) ||
            `Cart add failed (HTTP ${fetchResponse.status})`;

          this.dispatchEvent(
            new CartErrorEvent(this.id, msg, typeof parsed.description === 'string' ? parsed.description : undefined, parsed.errors)
          );

          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = msg;
            } else {
              addToCartTextError.appendChild(document.createTextNode(msg));
            }
            this.#setLiveRegionText(msg);

            this.#timeout = setTimeout(() => {
              addToCartTextError.classList.add('hidden');
              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          }

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
              productId: this.dataset.productId,
            })
          );
          return null;
        }

        return parsed;
      })
      .then(async (response) => {
        if (!response) return;

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText =
            addedTextElement?.textContent?.trim() ||
            (typeof Theme !== 'undefined' ? Theme?.translations?.added : undefined) ||
            'Added';
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

        let cart = await this.#fetchAndUpdateCartQuantity();
        if (!cart && typeof response.item_count === 'number') {
          cart = /** @type {Cart} */ (response);
        } else if (!cart && Array.isArray(response.items)) {
          cart = /** @type {Cart} */ ({ items: response.items });
        } else if (!cart && response.variant_id != null) {
          cart = /** @type {Cart} */ ({
            items: [response],
          });
        }
        this.dispatchEvent(
          new CartAddEvent(cart ?? undefined, this.id, {
            source: 'product-form-component',
            itemCount: totalQuantity,
            productId: this.dataset.productId,
          })
        );
      })
      .catch((error) => {
        console.error(error);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  #clearVariantChangeSafetyTimer() {
    if (this.#variantChangeSafetyTimer) {
      clearTimeout(this.#variantChangeSafetyTimer);
      this.#variantChangeSafetyTimer = 0;
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    this.#clearVariantChangeSafetyTimer();

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';
    // Quick-add (and similar) may render the variant id input disabled when the initial variant is unavailable.
    // After a swatch change, the value updates here but disabled was never cleared, so FormData omits `id` and add fails.
    variantId.disabled = !event.detail.resource?.available;

    const hiddenQty = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="quantity"]'));
    if (hiddenQty && !this.refs.quantitySelector) {
      const fetchedForm = event.detail.data.html.querySelector(
        `product-form-component[data-product-id="${this.dataset.productId}"]`
      );
      const newQty = fetchedForm?.querySelector('input[name="quantity"]');
      if (newQty instanceof HTMLInputElement) {
        hiddenQty.value = newQty.value;
      } else {
        // Card section HTML (section-rendering-product-card) has no quick-add form; sync min qty from variant JSON.
        const minFromVariant = /** @type {{ min?: number } | undefined} */ (event.detail.resource?.quantity_rule)?.min;
        if (typeof minFromVariant === 'number' && minFromVariant > 0) {
          hiddenQty.value = String(minFromVariant);
        }
      }
    }

    this.#variantChangeInProgress = false;

    if (this.#addToCartQueue.length > 0) {
      const queuedItems = [...this.#addToCartQueue];
      this.#addToCartQueue = [];
      this.#processBatchAddToCart(queuedItems);
    }

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      const resolvedMax = this.#readQuantityMaxFromFetchedInput(newQuantityInput);
      quantitySelector.updateConstraints(newQuantityInput.min, resolvedMax, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          const resolvedMax = this.#readQuantityMaxFromFetchedInput(newQuantityInputElement);
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            resolvedMax,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} _event */
  #onVariantSelected = (_event) => {
    this.#variantChangeInProgress = true;
    if (this.#variantChangeSafetyTimer) clearTimeout(this.#variantChangeSafetyTimer);
    this.#variantChangeSafetyTimer = window.setTimeout(() => {
      this.#variantChangeSafetyTimer = 0;
      if (this.#variantChangeInProgress) {
        this.#variantChangeInProgress = false;
        const queued = [...this.#addToCartQueue];
        this.#addToCartQueue = [];
        if (queued.length) {
          this.#processBatchAddToCart(queued);
        }
      }
    }, 4000);
  };

  /**
   * Section fetch after variant:selected failed or returned no variant JSON — avoid dead add-to-cart.
   * @param {CustomEvent<{ productId?: string }>} event
   */
  #onVariantFetchFailed = (event) => {
    const pid = event.detail?.productId;
    if (pid && pid !== this.dataset.productId) return;
    this.#clearVariantChangeSafetyTimer();
    if (!this.#variantChangeInProgress && this.#addToCartQueue.length === 0) return;
    this.#variantChangeInProgress = false;
    const queued = [...this.#addToCartQueue];
    this.#addToCartQueue = [];
    if (queued.length) {
      this.#processBatchAddToCart(queued);
    }
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
